import { api } from "./useApi";
import {
  InfoAssinatura,
  Fatura,
  DadosCartao,
  FormaPagamento,
  ResultadoAtualizarCartao,
  ResultadoGerarPix,
  ResultadoVerificarPix,
  ResultadoListarPlanos,
  ResultadoSimulacao,
  ResultadoTrocaPlano,
  ResultadoMudarFormaPagamento,
  ResultadoCancelarUpgrade,
} from "../types/Assinatura";

/**
 * Hook de API para a rota /assinatura (Etapa 3 — fluxo novo de assinatura).
 *
 * Encapsula as 4 chamadas ao conecta-painel-api:
 *   - getInfoAssinatura: discrimina fluxo (novo/legado) + retorna dados consolidados
 *   - listarFaturas: lista de ciclos (faturas) da assinatura
 *   - atualizarCartao: troca cartão + cobrança síncrona se houver ciclo aberto
 *   - gerarPixFatura: gera QR PIX pro ciclo aberto
 */
export const AssinaturaApi = () => ({
  getInfoAssinatura: async (): Promise<InfoAssinatura> => {
    const res = await api.post("usuario/get_info_assinatura");
    return res.data;
  },

  listarFaturas: async (): Promise<{ ok: boolean; faturas: Fatura[] }> => {
    const res = await api.post("pagarme/listar_faturas");
    return res.data;
  },

  atualizarCartao: async (
    cartao: DadosCartao,
    parcelas?: number
  ): Promise<ResultadoAtualizarCartao> => {
    const res = await api.post("pagarme/atualizar_cartao", { cartao, parcelas });
    return res.data;
  },

  gerarPixFatura: async (): Promise<ResultadoGerarPix> => {
    const res = await api.post("pagarme/gerar_pix_fatura");
    return res.data;
  },

  verificarPagamentoPix: async (): Promise<ResultadoVerificarPix> => {
    const res = await api.post("pagarme/verificar_pagamento_pix");
    return res.data;
  },

  listarPlanosDisponiveis: async (): Promise<ResultadoListarPlanos> => {
    const res = await api.post("pagarme/listar_planos_disponiveis");
    return res.data;
  },

  simularMudancaPlano: async (
    idPlanoNovo: number
  ): Promise<ResultadoSimulacao> => {
    const res = await api.post("pagarme/simular_mudanca_plano", {
      id_plano_novo: idPlanoNovo,
    });
    return res.data;
  },

  trocarPlano: async (
    idPlanoNovo: number,
    parcelas?: number,
    forma?: FormaPagamento,
    cartao?: DadosCartao
  ): Promise<ResultadoTrocaPlano> => {
    const res = await api.post("pagarme/trocar_plano", {
      id_plano_novo: idPlanoNovo,
      parcelas,
      forma,
      cartao,
    });
    return res.data;
  },

  cancelarMudancaPlano: async (): Promise<{
    ok: boolean;
    mensagem?: string;
    erro?: string;
  }> => {
    const res = await api.post("pagarme/cancelar_mudanca_plano");
    return res.data;
  },

  mudarFormaPagamento: async (
    forma: FormaPagamento,
    cartao?: DadosCartao
  ): Promise<ResultadoMudarFormaPagamento> => {
    const res = await api.post("pagarme/mudar_forma_pagamento", { forma, cartao });
    return res.data;
  },

  cancelarUpgradePendente: async (): Promise<ResultadoCancelarUpgrade> => {
    const res = await api.post("pagarme/cancelar_upgrade_pendente");
    return res.data;
  },

  consultarUpgradePendentePix: async (): Promise<{
    ok: boolean;
    pagarme_charge_id?: string;
    pagarme_status?: string;
    qr_code?: string | null;
    qr_code_url?: string | null;
    expires_at?: string | null;
    erro?: string;
  }> => {
    const res = await api.post("pagarme/consultar_upgrade_pendente_pix");
    return res.data;
  },
});
